import React from "react";

/**
 * Presentational acquisition/campaign dashboard shared by the client (per-venue)
 * and admin (cross-venue) reports. Purely driven by props — the parent owns data
 * fetching and filter state. Charts are hand-rolled inline SVG/CSS (the app ships
 * no chart library on purpose).
 *
 * Props:
 *   summary        summary object from the API (or null while loading)
 *   loading,error  summary request state
 *   members,membersTotal,membersLoading  detail-table state
 *   filters {from,to,granularity}  + setFilters
 *   drill {source?,medium?,campaign?} + setDrill  (table drill-down)
 *   onExport()     trigger CSV download of the current filter/drill set
 *   extraFilter    optional ReactNode rendered in the filter bar (admin venue picker)
 *   showVenue      include the venue column in the table + the byVenue panel
 *   title,subtitle header copy
 */
const PALETTE = ["#6366f1", "#06b6d4", "#f59e0b", "#ef4444", "#10b981", "#8b5cf6", "#ec4899", "#64748b"];

export default function AcquisitionDashboard({
  summary, loading, error,
  members, membersTotal, membersLoading,
  filters, setFilters,
  drill, setDrill,
  onExport,
  extraFilter, showVenue,
  title = "Acquisition", subtitle = "Where your signups come from — campaigns, channels & referrers",
}) {
  const set = (patch) => setFilters({ ...filters, ...patch });
  const hasDrill = drill && (drill.source != null || drill.medium != null || drill.campaign != null);

  return (
    <>
      <div className="venues-page-header">
        <div>
          <h1>{title}</h1>
          <p className="page-subtitle">{subtitle}</p>
        </div>
        <button className="btn btn-outline btn-sm" onClick={onExport} disabled={!summary}>Export CSV</button>
      </div>

      {/* ── Filter bar ───────────────────────────── */}
      <div className="settings-card" style={{ marginBottom: "1rem" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end" }}>
          {extraFilter}
          <div className="form-field" style={{ margin: 0 }}>
            <label>From</label>
            <input type="date" value={filters.from} onChange={(e) => set({ from: e.target.value })} />
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label>To</label>
            <input type="date" value={filters.to} onChange={(e) => set({ to: e.target.value })} />
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label>Group by</label>
            <div style={{ display: "flex", gap: 4 }}>
              {["day", "week", "month"].map((g) => (
                <button
                  key={g}
                  type="button"
                  className={`btn btn-sm ${filters.granularity === g ? "btn-primary" : "btn-outline"}`}
                  onClick={() => set({ granularity: g })}
                  style={{ textTransform: "capitalize" }}
                >{g}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {error && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{error}</div>}
      {loading && <div style={{ color: "var(--text-muted)" }}>Loading…</div>}

      {summary && (
        <>
          {/* ── Headline numbers ───────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
            <Stat label="Total signups" value={summary.total} />
            <Stat label="Attributed" value={summary.attributed} sub={pct(summary.attributed, summary.total)} />
            <Stat label="Direct / unknown" value={summary.direct} sub={pct(summary.direct, summary.total)} />
            <Stat label="With ad click ID" value={summary.clickIds?.with_click_id ?? 0} sub="gclid / fbclid" />
          </div>

          {/* ── Trend ──────────────────────────────── */}
          <div className="settings-card" style={{ marginBottom: "1rem" }}>
            <div className="settings-card-header"><h3 style={{ margin: 0 }}>Signups over time</h3></div>
            <TrendChart data={summary.trend} />
          </div>

          {/* ── Channel mix + referrers ────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
            <div className="settings-card">
              <div className="settings-card-header"><h3 style={{ margin: 0 }}>Channel mix</h3></div>
              <Donut data={summary.channelMix} />
            </div>
            <BarPanel title="Top referrers" rows={summary.topReferrers} empty="No referrer data in range." />
          </div>

          {/* ── Source / medium / campaign ─────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
            <BarPanel title="By source"   rows={summary.bySource}   onPick={(k) => setDrill({ source: k === "(none)" ? "(none)" : k })} activeKey={drill?.source} />
            <BarPanel title="By medium"   rows={summary.byMedium}   onPick={(k) => setDrill({ medium: k === "(none)" ? "(none)" : k })} activeKey={drill?.medium} />
            <BarPanel title="By campaign" rows={summary.byCampaign} onPick={(k) => setDrill({ campaign: k === "(none)" ? "(none)" : k })} activeKey={drill?.campaign} />
          </div>

          {showVenue && summary.byVenue && (
            <div style={{ marginBottom: "1rem" }}>
              <BarPanel title="By venue" rows={summary.byVenue.map((r) => ({ key: r.name, count: r.count }))} empty="No signups in range." />
            </div>
          )}

          {/* ── Detail table ───────────────────────── */}
          <div className="settings-card">
            <div className="settings-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: "0.5rem" }}>
              <div>
                <h3 style={{ margin: 0 }}>Signups {hasDrill && <span style={{ fontWeight: 400, fontSize: "0.85rem", color: "var(--text-muted)" }}>· filtered</span>}</h3>
                <p style={{ margin: "0.25rem 0 0", fontSize: "0.8rem" }}>{membersTotal?.toLocaleString() || 0} matching · showing {members?.length || 0}</p>
              </div>
              {hasDrill && <button className="btn btn-outline btn-sm" onClick={() => setDrill({})}>Clear drill-down</button>}
            </div>
            {hasDrill && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "0.75rem" }}>
                {["source", "medium", "campaign"].map((k) => drill[k] != null && (
                  <span key={k} style={{ fontSize: "0.75rem", padding: "0.15rem 0.5rem", borderRadius: 999, background: "var(--bg-elevated)", color: "var(--text-muted)" }}>
                    {k}: <strong style={{ color: "var(--text)" }}>{drill[k]}</strong>
                  </span>
                ))}
              </div>
            )}
            {membersLoading ? <div style={{ color: "var(--text-muted)" }}>Loading…</div> : (
              <div style={{ overflowX: "auto" }}>
                <table className="data-table" style={{ width: "100%", fontSize: "0.82rem" }}>
                  <thead>
                    <tr>
                      <th>Member #</th>
                      {showVenue && <th>Venue</th>}
                      <th>Name</th>
                      <th>Joined</th>
                      <th>Product</th>
                      <th>Source</th>
                      <th>Medium</th>
                      <th>Campaign</th>
                      <th>Referrer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members?.length ? members.map((m, i) => (
                      <tr key={i}>
                        <td style={{ fontVariantNumeric: "tabular-nums" }}>{m.membership_id}</td>
                        {showVenue && <td>{m.venue}</td>}
                        <td>{[m.first_name, m.last_name].filter(Boolean).join(" ")}</td>
                        <td>{fmtDate(m.date_of_joining || m.created_at)}</td>
                        <td>{m.product || <Muted>—</Muted>}</td>
                        <td>{m.utm_source || <Muted>—</Muted>}</td>
                        <td>{m.utm_medium || <Muted>—</Muted>}</td>
                        <td>{m.utm_campaign || <Muted>—</Muted>}</td>
                        <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.tracking_referrer || ""}>{m.tracking_referrer || <Muted>—</Muted>}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={showVenue ? 9 : 8} style={{ color: "var(--text-muted)", padding: "0.75rem" }}>No signups match these filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

const Muted = ({ children }) => <span style={{ color: "var(--text-muted)" }}>{children}</span>;

function Stat({ label, value, sub }) {
  return (
    <div className="settings-card" style={{ padding: "1rem" }}>
      <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{label}</div>
      <div style={{ fontSize: "1.6rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>{(value ?? 0).toLocaleString()}</div>
      {sub != null && <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>{sub}</div>}
    </div>
  );
}

function BarPanel({ title, rows, onPick, activeKey, empty = "No data." }) {
  const max = Math.max(...(rows || []).map((r) => Number(r.count || 0)), 1);
  return (
    <div className="settings-card">
      <div className="settings-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{rows?.length || 0}</span>
      </div>
      {rows?.length ? rows.map((r, i) => {
        const active = activeKey != null && (activeKey === r.key || (activeKey === "(none)" && r.key === "(none)"));
        return (
          <div
            key={i}
            onClick={onPick ? () => onPick(r.key) : undefined}
            style={{ margin: "0.4rem 0", cursor: onPick ? "pointer" : "default", opacity: activeKey != null && !active ? 0.55 : 1 }}
            title={onPick ? "Filter the table by this value" : undefined}
          >
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.83rem", marginBottom: "0.2rem" }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "75%", fontWeight: active ? 700 : 400 }}>{r.key}</span>
              <span style={{ fontVariantNumeric: "tabular-nums", color: "var(--text-muted)" }}>{r.count}</span>
            </div>
            <div style={{ height: 5, borderRadius: 999, background: "var(--bg-elevated)" }}>
              <div style={{ height: "100%", width: `${(Number(r.count) / max) * 100}%`, background: "var(--accent)", borderRadius: 999 }} />
            </div>
          </div>
        );
      }) : <div style={{ color: "var(--text-muted)" }}>{empty}</div>}
    </div>
  );
}

function Donut({ data }) {
  const rows = (data || []).filter((r) => Number(r.count) > 0);
  const total = rows.reduce((s, r) => s + Number(r.count), 0);
  if (!total) return <div style={{ color: "var(--text-muted)" }}>No data in range.</div>;
  const r = 52, c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
      <svg width="130" height="130" viewBox="0 0 130 130">
        <g transform="rotate(-90 65 65)">
          {rows.map((row, i) => {
            const frac = Number(row.count) / total;
            const seg = (
              <circle
                key={i}
                cx="65" cy="65" r={r} fill="none"
                stroke={PALETTE[i % PALETTE.length]} strokeWidth="18"
                strokeDasharray={`${frac * c} ${c - frac * c}`}
                strokeDashoffset={-offset * c}
              />
            );
            offset += frac;
            return seg;
          })}
        </g>
        <text x="65" y="60" textAnchor="middle" style={{ fontSize: 18, fontWeight: 700, fill: "var(--text)" }}>{total.toLocaleString()}</text>
        <text x="65" y="78" textAnchor="middle" style={{ fontSize: 10, fill: "var(--text-muted)" }}>signups</text>
      </svg>
      <div style={{ flex: 1, minWidth: 140 }}>
        {rows.map((row, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.82rem", margin: "0.25rem 0" }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: PALETTE[i % PALETTE.length], flexShrink: 0 }} />
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.key}</span>
            <span style={{ color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>{Math.round((Number(row.count) / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Trend as a column/bar chart — renders cleanly for 1 day or 90 (an SVG line
// gets distorted when stretched with only a point or two). Bars share the width
// evenly up to a cap, scroll horizontally once there are too many to fit.
function TrendChart({ data }) {
  const rows = data || [];
  if (!rows.length) return <div style={{ color: "var(--text-muted)" }}>No signups in range.</div>;
  const n = rows.length;
  const max = Math.max(...rows.map((r) => Number(r.count)), 1);
  const showCounts = n <= 31;                 // value labels get noisy past a month of daily bars
  const labelEvery = Math.max(1, Math.ceil(n / 10));
  const tightGap = n > 40;
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: "1rem" }}>
      <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>peak {max}</div>
      <div style={{ overflowX: "auto", flex: 1 }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: n < 8 ? "flex-start" : "space-between", gap: tightGap ? 2 : 6, height: 176, minWidth: tightGap ? n * 10 : undefined }}>
          {rows.map((r, i) => {
            const h = Math.max(3, Math.round((Number(r.count) / max) * 130));
            const showLabel = i === 0 || i === n - 1 || i % labelEvery === 0;
            return (
              <div key={i} title={`${r.period}: ${r.count}`}
                   style={{ flex: "1 1 0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", minWidth: 6, maxWidth: 56, height: "100%" }}>
                <span style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginBottom: 2, fontVariantNumeric: "tabular-nums", height: 14, visibility: showCounts ? "visible" : "hidden" }}>{r.count}</span>
                <div style={{ width: "70%", maxWidth: 40, minWidth: 4, height: h, background: "var(--accent)", borderRadius: "4px 4px 0 0" }} />
                <span style={{ fontSize: "0.64rem", color: "var(--text-muted)", marginTop: 5, whiteSpace: "nowrap", visibility: showLabel ? "visible" : "hidden" }}>{shortPeriod(r.period)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
function shortPeriod(p) {
  // periods arrive as YYYY-MM-DD (day / week-start / month-start) → DD/MM
  const parts = String(p).split("-");
  return parts.length === 3 ? `${parts[2]}/${parts[1]}` : String(p);
}

function pct(part, whole) {
  if (!whole) return "0%";
  return `${Math.round((part / whole) * 100)}%`;
}
function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return String(v).slice(0, 10);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}
